/* ==============================================================================
   POLYMERCRAFTS — MATERIAL WEIGHT CALCULATOR ENGINE
   File: assets/weight-calculator.js
   Supports 8 Cross Sections, 35+ Materials, 6 Dimensional Units (mm, cm, m, in, ft, yd),
   4 Density Units, Dynamic Technical SVGs, and Live Results.
   ============================================================================== */

(function () {
  'use strict';

  // 1. Material Database with Densities in g/cm³ (g/cc)
  const MATERIALS = {
    // Engineering Plastics
    'Acrylic (PMMA)': 1.19,
    'ABS': 1.07,
    'Acetal POM C': 1.41,
    'Nylon 6': 1.14,
    'Nylon 66': 1.15,
    'Nylon Cast': 1.16,
    'Nylon 66 30GF': 1.35,
    'PEEK': 1.32,
    'PEEK 30GF': 1.51,
    'PET': 1.38,
    'Polycarbonate': 1.20,
    'Polyethylene (HDPE)': 0.95,
    'Polyethylene (UHMWPE)': 0.93,
    'Polypropylene': 0.91,
    'Polyurethane': 1.20,
    'PTFE': 2.18,
    'PVDF': 1.78,
    'PVC Rigid': 1.40,
    'Bakelite / Hylam': 1.38,
    'Epoxy Glass (FR4)': 1.85,
    
    // Metals & Alloys
    'Aluminum 6061': 2.70,
    'Aluminum-Bronze': 7.65,
    'Beryllium': 1.85,
    'Brass CZ121': 8.47,
    'Cast Iron': 7.20,
    'Copper': 8.96,
    'Gold': 19.32,
    'Lead': 11.34,
    'Magnesium': 1.74,
    'Mercury': 13.55,
    'Nickel-Cobalt Alloys': 8.44,
    'Platinum': 21.45,
    'Steel': 7.85,
    'Stainless 304': 8.00,
    'Silver': 10.49,
    'Titanium': 4.43,
    'Tungsten': 19.25,
    'Zinc': 7.14,
    'Zirconium': 6.52,
    
    // Minerals & Others
    'Alumina (Al2O3)': 3.95,
    'Glass': 2.50,
    'Granite / Marble': 2.70,
    'Water (H2O)': 1.00,
    'Custom': 1.00
  };

  // 2. Unit Conversions to cm
  const LENGTH_TO_CM = {
    'mm': 0.1,
    'cm': 1.0,
    'metre': 100.0,
    'm': 100.0,
    'inch': 2.54,
    'in': 2.54,
    'foot': 30.48,
    'ft': 30.48,
    'yard': 91.44,
    'yd': 91.44
  };

  // 3. Density Conversions from g/cc
  const DENSITY_CONVERSIONS = {
    'g/cc': 1.0,
    'kg/m³': 1000.0,
    'lb/in³': 0.036127292,
    'lb/ft³': 62.42796
  };

  // 4. SVG Technical Diagrams for each cross section
  const DIAGRAMS = {
    'sheet': `
      <svg viewBox="0 0 260 140" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- 3D Sheet Block -->
        <polygon points="40,80 160,80 220,35 100,35" fill="#4B5563" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="40,80 160,80 160,95 40,95" fill="#9CA3AF" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="160,80 220,35 220,50 160,95" fill="#6B7280" stroke="#1F2937" stroke-width="1.5"/>
        
        <!-- Length Arrow (Red) -->
        <line x1="85" y1="25" x2="205" y2="25" stroke="#DC2626" stroke-width="3" marker-end="url(#red-arrow-end)" marker-start="url(#red-arrow-start)"/>
        <text x="145" y="18" fill="#DC2626" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">LENGTH</text>
        
        <!-- Width Arrow (Blue) -->
        <line x1="25" y1="88" x2="175" y2="88" stroke="#2563EB" stroke-width="3" marker-end="url(#blue-arrow-end)" marker-start="url(#blue-arrow-start)"/>
        <text x="195" y="92" fill="#2563EB" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="start">WIDTH</text>
        
        <!-- Thickness Arrow (Yellow) -->
        <line x1="28" y1="78" x2="28" y2="97" stroke="#D97706" stroke-width="3" marker-end="url(#yellow-arrow-end)" marker-start="url(#yellow-arrow-start)"/>
        <text x="75" y="122" fill="#D97706" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">THICKNESS</text>
        
        <!-- Marker Definitions -->
        <defs>
          <marker id="red-arrow-end" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 1 L 10 5 L 0 9 z" fill="#DC2626"/></marker>
          <marker id="red-arrow-start" viewBox="0 0 10 10" refX="4" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 10 1 L 0 5 L 10 9 z" fill="#DC2626"/></marker>
          <marker id="blue-arrow-end" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 1 L 10 5 L 0 9 z" fill="#2563EB"/></marker>
          <marker id="blue-arrow-start" viewBox="0 0 10 10" refX="4" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 10 1 L 0 5 L 10 9 z" fill="#2563EB"/></marker>
          <marker id="yellow-arrow-end" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 1 L 10 5 L 0 9 z" fill="#D97706"/></marker>
          <marker id="yellow-arrow-start" viewBox="0 0 10 10" refX="4" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 10 1 L 0 5 L 10 9 z" fill="#D97706"/></marker>
        </defs>
      </svg>
    `,
    'square': `
      <svg viewBox="0 0 260 140" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- 3D Square Bar -->
        <polygon points="50,60 80,60 80,90 50,90" fill="#9CA3AF" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="50,60 80,60 200,25 170,25" fill="#4B5563" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="80,60 200,25 200,55 80,90" fill="#6B7280" stroke="#1F2937" stroke-width="1.5"/>
        
        <!-- Length Arrow (Red) -->
        <line x1="85" y1="20" x2="195" y2="20" stroke="#DC2626" stroke-width="3" marker-end="url(#red-arrow-end)" marker-start="url(#red-arrow-start)"/>
        <text x="140" y="14" fill="#DC2626" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">LENGTH</text>
        
        <!-- Width/Side Arrow (Blue) -->
        <line x1="50" y1="100" x2="80" y2="100" stroke="#2563EB" stroke-width="3" marker-end="url(#blue-arrow-end)" marker-start="url(#blue-arrow-start)"/>
        <text x="65" y="118" fill="#2563EB" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">WIDTH / SIDE</text>
      </svg>
    `,
    'round-bar': `
      <svg viewBox="0 0 260 140" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- 3D Solid Cylinder -->
        <path d="M 60,40 L 190,25 L 190,75 L 60,90 Z" fill="#6B7280" stroke="#1F2937" stroke-width="1.5"/>
        <ellipse cx="60" cy="65" rx="18" ry="25" fill="#9CA3AF" stroke="#1F2937" stroke-width="1.5"/>
        <ellipse cx="190" cy="50" rx="18" ry="25" fill="#4B5563" stroke="#1F2937" stroke-width="1.5"/>
        
        <!-- Length Arrow (Red) -->
        <line x1="75" y1="20" x2="185" y2="20" stroke="#DC2626" stroke-width="3" marker-end="url(#red-arrow-end)" marker-start="url(#red-arrow-start)"/>
        <text x="130" y="14" fill="#DC2626" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">LENGTH</text>
        
        <!-- Diameter Arrow (Green) -->
        <line x1="28" y1="40" x2="28" y2="90" stroke="#16A34A" stroke-width="3" marker-end="url(#green-arrow-end)" marker-start="url(#green-arrow-start)"/>
        <text x="18" y="115" fill="#16A34A" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="start">DIAMETER (Ø)</text>
        
        <defs>
          <marker id="green-arrow-end" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 0 1 L 10 5 L 0 9 z" fill="#16A34A"/></marker>
          <marker id="green-arrow-start" viewBox="0 0 10 10" refX="4" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M 10 1 L 0 5 L 10 9 z" fill="#16A34A"/></marker>
        </defs>
      </svg>
    `,
    'round-tube': `
      <svg viewBox="0 0 260 140" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- 3D Hollow Tube -->
        <path d="M 60,40 L 190,25 L 190,75 L 60,90 Z" fill="#6B7280" stroke="#1F2937" stroke-width="1.5"/>
        <ellipse cx="60" cy="65" rx="20" ry="26" fill="#9CA3AF" stroke="#1F2937" stroke-width="1.5"/>
        <ellipse cx="60" cy="65" rx="11" ry="15" fill="#1F2937" stroke="#111827" stroke-width="1.5"/>
        <ellipse cx="190" cy="50" rx="20" ry="26" fill="#4B5563" stroke="#1F2937" stroke-width="1.5"/>
        
        <!-- Length Arrow (Red) -->
        <line x1="75" y1="20" x2="185" y2="20" stroke="#DC2626" stroke-width="3" marker-end="url(#red-arrow-end)" marker-start="url(#red-arrow-start)"/>
        <text x="130" y="14" fill="#DC2626" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">LENGTH</text>
        
        <!-- Outer Diameter Arrow (Green) -->
        <line x1="24" y1="38" x2="24" y2="92" stroke="#16A34A" stroke-width="2.5" marker-end="url(#green-arrow-end)" marker-start="url(#green-arrow-start)"/>
        <text x="10" y="112" fill="#16A34A" font-family="JetBrains Mono, monospace" font-size="10" font-weight="700">OUTER Ø</text>
        
        <!-- Wall Thickness (Yellow) -->
        <line x1="60" y1="79" x2="60" y2="91" stroke="#D97706" stroke-width="2.5" marker-end="url(#yellow-arrow-end)" marker-start="url(#yellow-arrow-start)"/>
        <text x="90" y="112" fill="#D97706" font-family="JetBrains Mono, monospace" font-size="10" font-weight="700">WALL THICKNESS</text>
      </svg>
    `,
    'hexagon': `
      <svg viewBox="0 0 260 140" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- 3D Hexagonal Bar -->
        <polygon points="45,55 65,42 85,55 85,75 65,88 45,75" fill="#9CA3AF" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="65,42 85,55 205,25 185,12" fill="#4B5563" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="85,55 85,75 205,45 205,25" fill="#6B7280" stroke="#1F2937" stroke-width="1.5"/>
        
        <!-- Length Arrow (Red) -->
        <line x1="85" y1="12" x2="195" y2="12" stroke="#DC2626" stroke-width="3" marker-end="url(#red-arrow-end)" marker-start="url(#red-arrow-start)"/>
        <text x="140" y="8" fill="#DC2626" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">LENGTH</text>
        
        <!-- Hexagon A/F or Points Arrow -->
        <line x1="32" y1="42" x2="32" y2="88" stroke="#2563EB" stroke-width="2.5" marker-end="url(#blue-arrow-end)" marker-start="url(#blue-arrow-start)"/>
        <text x="65" y="116" fill="#2563EB" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">HEXAGON A/F / POINTS</text>
      </svg>
    `,
    'octagon': `
      <svg viewBox="0 0 260 140" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
        <!-- 3D Octagonal Bar -->
        <polygon points="45,52 57,40 73,40 85,52 85,68 73,80 57,80 45,68" fill="#9CA3AF" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="57,40 73,40 193,15 177,15" fill="#4B5563" stroke="#1F2937" stroke-width="1.5"/>
        <polygon points="73,40 85,52 205,27 193,15" fill="#6B7280" stroke="#1F2937" stroke-width="1.5"/>
        
        <!-- Length Arrow (Red) -->
        <line x1="85" y1="12" x2="195" y2="12" stroke="#DC2626" stroke-width="3" marker-end="url(#red-arrow-end)" marker-start="url(#red-arrow-start)"/>
        <text x="140" y="8" fill="#DC2626" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">LENGTH</text>
        
        <!-- Octagon Arrow -->
        <line x1="32" y1="40" x2="32" y2="80" stroke="#2563EB" stroke-width="2.5" marker-end="url(#blue-arrow-end)" marker-start="url(#blue-arrow-start)"/>
        <text x="65" y="116" fill="#2563EB" font-family="JetBrains Mono, monospace" font-size="11" font-weight="700" text-anchor="middle">OCTAGON A/F / POINTS</text>
      </svg>
    `
  };

  function initWeightCalculator(scope = document) {
    const calcSections = scope.querySelectorAll('.sec__pc-weight-calc');

    calcSections.forEach((section) => {
      const materialSelect = section.querySelector('.pc-material-select');
      const densityInput = section.querySelector('.pc-density-input');
      const densityUnitSelect = section.querySelector('.pc-density-unit-select');
      const shapeSelect = section.querySelector('.pc-shape-select');
      const qtyInput = section.querySelector('.pc-qty-input');

      // Dimension containers & inputs
      const thicknessGroup = section.querySelector('.pc-group-thickness');
      const thicknessInput = section.querySelector('.pc-thickness-input');
      const thicknessUnit = section.querySelector('.pc-thickness-unit');
      const thicknessLabel = section.querySelector('.pc-label-thickness');

      const widthGroup = section.querySelector('.pc-group-width');
      const widthInput = section.querySelector('.pc-width-input');
      const widthUnit = section.querySelector('.pc-width-unit');
      const widthLabel = section.querySelector('.pc-label-width');

      const lengthGroup = section.querySelector('.pc-group-length');
      const lengthInput = section.querySelector('.pc-length-input');
      const lengthUnit = section.querySelector('.pc-length-unit');

      const wallGroup = section.querySelector('.pc-group-wall');
      const wallInput = section.querySelector('.pc-wall-input');
      const wallUnit = section.querySelector('.pc-wall-unit');

      // Buttons
      const calcBtn = section.querySelector('.btn-pc-calc-primary');
      const resetBtn = section.querySelector('.btn-pc-calc-reset');
      const copyBtn = section.querySelector('.btn-pc-copy-result');
      const quoteBtn = section.querySelector('.btn-pc-quote-action');

      // Output Results
      const resGrams = section.querySelector('.pc-res-grams');
      const resKg = section.querySelector('.pc-res-kg');
      const resLbs = section.querySelector('.pc-res-lbs');
      const resOz = section.querySelector('.pc-res-oz');
      const resTonnes = section.querySelector('.pc-res-tonnes');

      // Diagram Canvas & Badge
      const diagramCanvas = section.querySelector('.pc-diagram-canvas');
      const diagramBadge = section.querySelector('.pc-diagram-badge');

      if (!materialSelect || !shapeSelect) return;

      // 1. Handle Material Change -> Update Density Input with standard density
      function onMaterialChange() {
        const mat = materialSelect.value;
        const baseDensity = MATERIALS[mat] !== undefined ? MATERIALS[mat] : 1.0;
        densityInput.value = baseDensity;
        calculate();
      }

      // 2. Handle Density Unit Change (Density value remains unchanged)
      function onDensityUnitChange() {
        calculate();
      }

      // 3. Handle Cross Section Shape Change -> Update Form Fields & Technical Diagram
      function onShapeChange() {
        const shape = shapeSelect.value;

        // Reset visibility
        thicknessGroup.style.display = 'none';
        widthGroup.style.display = 'none';
        lengthGroup.style.display = 'flex';
        if (wallGroup) wallGroup.style.display = 'none';

        if (shape === 'sheet') {
          thicknessGroup.style.display = 'flex';
          thicknessLabel.textContent = 'Thickness:';
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Width:';
          diagramBadge.textContent = 'Sheet / Plate / Flats';
          diagramCanvas.innerHTML = DIAGRAMS['sheet'];
        } else if (shape === 'square') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Width / Side:';
          diagramBadge.textContent = 'Square Bar';
          diagramCanvas.innerHTML = DIAGRAMS['square'];
        } else if (shape === 'round-bar') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Diameter (Ø):';
          diagramBadge.textContent = 'Round Bar';
          diagramCanvas.innerHTML = DIAGRAMS['round-bar'];
        } else if (shape === 'round-tube') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Outer Diameter (Ø):';
          if (wallGroup) wallGroup.style.display = 'flex';
          diagramBadge.textContent = 'Round Tube';
          diagramCanvas.innerHTML = DIAGRAMS['round-tube'];
        } else if (shape === 'hexagon-af') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Across Flats (A/F):';
          diagramBadge.textContent = 'Hexagon Across Flats';
          diagramCanvas.innerHTML = DIAGRAMS['hexagon'];
        } else if (shape === 'hexagon-points') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Across Points / Corners:';
          diagramBadge.textContent = 'Hexagon Across Points';
          diagramCanvas.innerHTML = DIAGRAMS['hexagon'];
        } else if (shape === 'octagon-af') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Across Flats (A/F):';
          diagramBadge.textContent = 'Octagon Across Flats';
          diagramCanvas.innerHTML = DIAGRAMS['octagon'];
        } else if (shape === 'octagon-points') {
          widthGroup.style.display = 'flex';
          widthLabel.textContent = 'Across Points / Corners:';
          diagramBadge.textContent = 'Octagon Across Points';
          diagramCanvas.innerHTML = DIAGRAMS['octagon'];
        }

        calculate();
      }

      // Convert value + unit to cm
      function toCm(val, unit) {
        const factor = LENGTH_TO_CM[unit] || 0.1;
        return (parseFloat(val) || 0) * factor;
      }

      // 4. Main Calculation Function
      function calculate() {
        const shape = shapeSelect.value;
        const qty = Math.max(1, parseInt(qtyInput.value) || 1);

        // Density in g/cc
        const densityVal = parseFloat(densityInput.value) || 0;
        const densityUnit = densityUnitSelect.value;
        let densityGcc = densityVal;

        if (densityUnit === 'kg/m³') {
          // If user explicitly entered >= 100 (e.g. 1070), convert to g/cc; otherwise if standard value like 1.07, treat as specific gravity
          densityGcc = densityVal >= 100 ? densityVal / 1000.0 : densityVal;
        } else if (densityUnit === 'lb/in³') {
          densityGcc = densityVal / 0.036127292;
        } else if (densityUnit === 'lb/ft³') {
          densityGcc = densityVal >= 10 ? densityVal / 62.42796 : densityVal;
        } else {
          densityGcc = densityVal;
        }

        // Dimensions in cm
        const L = toCm(lengthInput.value, lengthUnit.value);
        const W = toCm(widthInput.value, widthUnit.value);
        const T = toCm(thicknessInput.value, thicknessUnit.value);
        const Wall = wallInput ? toCm(wallInput.value, wallUnit.value) : 0;

        let volumeCm3 = 0;

        if (shape === 'sheet') {
          // Sheet / Plate: T * W * L
          volumeCm3 = T * W * L * qty;
        } else if (shape === 'square') {
          // Square Bar: Side^2 * L
          volumeCm3 = W * W * L * qty;
        } else if (shape === 'round-bar') {
          // Round Bar: pi * r^2 * L
          const r = W / 2.0;
          volumeCm3 = Math.PI * r * r * L * qty;
        } else if (shape === 'round-tube') {
          // Round Tube: pi * (R^2 - r^2) * L
          const R = W / 2.0;
          const r = Math.max(0, R - Wall);
          volumeCm3 = Math.PI * (R * R - r * r) * L * qty;
        } else if (shape === 'hexagon-af') {
          // Hexagon Across Flats: Area = (sqrt(3)/2) * s^2
          const area = (Math.sqrt(3) / 2.0) * (W * W);
          volumeCm3 = area * L * qty;
        } else if (shape === 'hexagon-points') {
          // Hexagon Across Points d: Area = (3*sqrt(3)/8) * d^2
          const area = ((3 * Math.sqrt(3)) / 8.0) * (W * W);
          volumeCm3 = area * L * qty;
        } else if (shape === 'octagon-af') {
          // Octagon Across Flats: Area = 2*(sqrt(2)-1) * s^2
          const area = 2.0 * (Math.sqrt(2) - 1.0) * (W * W);
          volumeCm3 = area * L * qty;
        } else if (shape === 'octagon-points') {
          // Octagon Across Points d: Area = (d^2) / sqrt(2)
          const area = (W * W) / Math.sqrt(2);
          volumeCm3 = area * L * qty;
        }

        // Total Weight in Grams
        const totalGrams = volumeCm3 * densityGcc;
        const totalKg = totalGrams / 1000.0;
        const totalLbs = totalGrams * 0.00220462262;
        const totalOz = totalGrams * 0.0352739619;
        const totalTonnes = totalKg / 1000.0;

        // Render Formatted Results
        function formatNumber(num) {
          if (num === 0 || isNaN(num)) return '0.00';
          if (num < 0.001) return num.toExponential(4);
          if (num < 1) return num.toFixed(4);
          if (num < 100) return num.toFixed(3);
          return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        if (resGrams) resGrams.textContent = formatNumber(totalGrams);
        if (resKg) resKg.textContent = formatNumber(totalKg);
        if (resLbs) resLbs.textContent = formatNumber(totalLbs);
        if (resOz) resOz.textContent = formatNumber(totalOz);
        if (resTonnes) resTonnes.textContent = formatNumber(totalTonnes);

        // Update Quote CTA Link with parameters
        if (quoteBtn) {
          const matName = encodeURIComponent(materialSelect.value);
          const profileName = encodeURIComponent(shapeSelect.options[shapeSelect.selectedIndex].text);
          const weightText = encodeURIComponent(`${formatNumber(totalKg)} kg (${formatNumber(totalLbs)} lbs)`);
          quoteBtn.href = `/pages/contact?material=${matName}&profile=${profileName}&weight=${weightText}&qty=${qty}`;
        }
      }

      // 5. Reset All Form Fields to Defaults
      function resetAll() {
        materialSelect.value = 'Acrylic (PMMA)';
        densityUnitSelect.value = 'g/cc';
        shapeSelect.value = 'sheet';
        qtyInput.value = '1';
        
        thicknessInput.value = '10';
        thicknessUnit.value = 'mm';
        
        widthInput.value = '100';
        widthUnit.value = 'mm';
        
        lengthInput.value = '1000';
        lengthUnit.value = 'mm';

        if (wallInput) {
          wallInput.value = '2';
          wallUnit.value = 'mm';
        }

        onShapeChange();
        onMaterialChange();
      }

      // 6. Copy Result to Clipboard
      if (copyBtn) {
        copyBtn.addEventListener('click', () => {
          const textToCopy = `Polymercrafts Weight Estimate:\nMaterial: ${materialSelect.value}\nProfile: ${shapeSelect.options[shapeSelect.selectedIndex].text}\nTotal Weight: ${resKg.textContent} kg (${resLbs.textContent} lbs / ${resGrams.textContent} g)`;
          navigator.clipboard.writeText(textToCopy).then(() => {
            const originalText = copyBtn.innerHTML;
            copyBtn.innerHTML = '<i class="fa-solid fa-check"></i> Copied!';
            setTimeout(() => { copyBtn.innerHTML = originalText; }, 2000);
          });
        });
      }

      // Event Listeners for Live Calculation
      materialSelect.addEventListener('change', onMaterialChange);
      densityUnitSelect.addEventListener('change', onDensityUnitChange);
      shapeSelect.addEventListener('change', onShapeChange);

      const liveInputs = [
        densityInput, qtyInput,
        thicknessInput, thicknessUnit,
        widthInput, widthUnit,
        lengthInput, lengthUnit,
        wallInput, wallUnit
      ];

      liveInputs.forEach(el => {
        if (el) {
          el.addEventListener('input', calculate);
          el.addEventListener('change', calculate);
        }
      });

      if (calcBtn) calcBtn.addEventListener('click', calculate);
      if (resetBtn) resetBtn.addEventListener('click', resetAll);

      // Initial Setup
      onShapeChange();
      onMaterialChange();
    });
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWeightCalculator());
  } else {
    initWeightCalculator();
  }

  // Shopify Theme Customizer Support
  document.addEventListener('shopify:section:load', (e) => initWeightCalculator(e.target));
})();
